import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FlexRow, FlexRowSummary } from '@app/flex-framework/flex-api.model';
import {
  LayoutConfig,
  SectionDisplayType,
} from '@app/flex-framework/flex-section-api.model';
import { FlexRowService } from '@app/flex-framework/services/flex-row.service';
import { TargetType } from '@app/shared/models/core-api.model';
import { markUnchangedStringAsPristine } from '@app/shared/utils';
import { isEmptyValidator } from '@app/shared/validators/is-empty.validator';
import {
  DfFormFieldBuilder,
  DfFormFieldConfig,
  NotificationType,
} from '@lib/fresco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'dgx-edit-row-details-modal',
  templateUrl: './edit-row-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditRowDetailsModal implements AfterViewInit, OnInit {
  public form: FormGroup = new FormGroup({});
  public options: FormlyFormOptions = {};
  public fields: DfFormFieldConfig[] = [];
  public model = {
    name: undefined,
    description: undefined,
    displayType: undefined,
  };

  public submitting: boolean = false;
  public i18n = this.translate.instant([
    'Core_Save',
    'OrgPlans_CustomTitlesAndDescriptionsNotLocalized',
    'dgFlexRow_EditSectionDescription',
    'dgFlexRow_EditSectionDisplayOptions',
    'dgFlexRow_EditSectionDetails',
    'dgFlexRow_EditSectionTitle',
    'dgFlexRow_FieldRequired',
    'dgFlexRow_DeleteConfirmSubmit',
    'dgFlexRow_Compact',
    'dgFlexRow_ShowCompactDescription', // Updated description text (PD-79055)
    'dgFlexRow_Expanded',
    'dgFlexRow_AllDescription',
    'dgFlexRow_TextTitlePlaceholder',
  ]);
  public displayOptions: LayoutConfig[] = [
    {
      displayType: SectionDisplayType.Single,
      displayName: this.i18n.dgFlexRow_Compact,
      description: this.i18n.dgFlexRow_ShowCompactDescription,
    },
    {
      displayType: SectionDisplayType.All,
      displayName: this.i18n.dgFlexRow_Expanded,
      description: this.i18n.dgFlexRow_AllDescription,
    },
  ];
  public submitButtonTxt = this.i18n.dgFlexRow_DeleteConfirmSubmit;

  @Input() public row: FlexRow;
  @Input() public targetType: TargetType;
  @Input() public sectionDefaultTitle: string;

  @ViewChild('markdown') public markdownRef: TemplateRef<any>;
  @ViewChild('displayType') public displayTypeRef: TemplateRef<any>;
  @ViewChild('removeButton') public removeButtonRef: TemplateRef<any>;

  public readonly NotificationType = NotificationType;

  constructor(
    private activeModal: NgbActiveModal,
    private builder: DfFormFieldBuilder,
    private cdr: ChangeDetectorRef,
    private flexRowService: FlexRowService,
    private translate: TranslateService
  ) {}

  public ngOnInit() {
    this.model = {
      name: this.row.section.name,
      description: this.row.section.description,
      displayType: this.row.section.layoutConfig.displayType,
    };

    if (!this.model.displayType || this.model.displayType === 'Double') {
      // Seems like this might not be set in some cases and also if the previous display type was Double
      // this wont get set properly so we fallback to the compact as default.
      this.model.displayType = SectionDisplayType.Single;
    }
  }

  public ngAfterViewInit() {
    this.fields = [
      this.builder
        .requiredTextInput('name', this.i18n.dgFlexRow_EditSectionTitle)
        .withMaxLength(255)
        .withDgatId('editSectionSettingsModal-818')
        .validatedBy(isEmptyValidator)
        .onControlValueChanges((value: string, model, formControl) =>
          markUnchangedStringAsPristine(
            this.row.section.name,
            value,
            model,
            formControl
          )
        )
        .updatedOn('change')
        .autofocused()
        .build(),
      this.builder
        .customField(
          'description',
          this.i18n.dgFlexRow_EditSectionDescription,
          this.markdownRef
        )
        .onControlValueChanges((value: string, model, formControl) =>
          markUnchangedStringAsPristine(
            this.row.section.description,
            value,
            model,
            formControl
          )
        )
        .updatedOn('change')
        .withDgatId('editSectionSettingsModal-e10')
        .build(),
      this.builder
        .customField('displayType', '', this.displayTypeRef)
        .updatedOn('change')
        .build(),
      this.builder
        .customField('', '', this.removeButtonRef)
        .styledBy('form__col-1')
        .build(),
    ];

    this.cdr.detectChanges();
  }

  public isDirectory(): boolean {
    return this.targetType === 'Directory';
  }

  public submit() {
    // Prevent duplicate submissions.
    if (this.submitting) {
      return;
    }
    this.submitting = true;
    this.form.markAsDirty();
    // Prevent invalid submissions.
    if (this.form.invalid) {
      this.submitting = false;
      this.cdr.markForCheck();
      return;
    }

    const updatedSection: FlexRowSummary = {
      ...this.row.section,
      // Trim the values before submitting them.
      // Formly also has parsers, but they don't appear to work any better
      // than simply trimming once here.
      name: this.model.name?.trim(),
      description: this.model.description?.trim(),
      layoutConfig: {
        displayType: this.model.displayType,
      },
    };

    this.activeModal.close(updatedSection);
  }

  public onDisplayTypeChange(
    formControl: AbstractControl,
    option: LayoutConfig
  ): void {
    formControl.setValue(option.displayType);
    formControl.markAsDirty();
  }

  public onDismiss() {
    this.activeModal.dismiss();
  }

  /**
   * Opens a custom sub-modal to confirm the user intends to delete
   * this row.
   */
  public onRemoveRow(): Subscription {
    return this.flexRowService.removeRow(
      this.row.section,
      this.sectionDefaultTitle,
      this.activeModal
    );
  }
}
